import { nodeCrypto } from "./node-crypto-lazy.ts";

import {
  SourceStructureSelectionV1Schema,
  admissionContentHash,
  resolveOpenPencilSourceStructureSelection,
  type SourceStructureSelectionMemberV1,
  type SourceStructureSelectionV1,
} from "./admission-v2.ts";
import {
  type CanonicalComponentRole,
  mapComponentRole,
} from "./component-role-registry.ts";
import {
  getPreflightOpenPencilOntologyFactsV2,
  openVerifiedOpenPencilComponentReadbackV2,
  preflightOpenPencilComponentBoundaryV2,
  type OpenPencilOntologyBoundaryInputV2,
  type OpenPencilOntologySourceRefsV2,
} from "./ontology-candidate-boundary-v2.ts";
import {
  type OpaqueId,
  type Sha256,
  type StructuralFact,
} from "./fact-shards.ts";

const NAMESPACE = "cogo.openpencil.structural.v1";
const OPAQUE_PATTERN = /^hmac-sha256:[a-f0-9]{64}$/;

export interface OpenPencilComponentOntologyCandidateInputV2 extends OpenPencilOntologyBoundaryInputV2 {
  allowedComponentSetOpaqueIds: OpaqueId[];
}

export interface OpenPencilComponentOntologyCandidateV2 {
  schemaVersion: "cogo.openpencil-component-ontology-candidate.v2";
  designations: Array<{
    sourceSemanticRef: OpaqueId;
    pageSemanticRef: OpaqueId;
    canonicalRole: CanonicalComponentRole | null;
    observedVariantCount: number;
    propertyAxisSemanticRefs: OpaqueId[];
    semanticSignature: Sha256;
    disposition: "candidate" | "unmapped" | "ambiguous";
  }>;
  sourceRefs: OpenPencilOntologySourceRefsV2;
  authority: {
    candidateOnly: true;
    generationEligible: false;
  };
}

type JsonRecord = Record<string, unknown>;
type Primitive = string | number | boolean | null;

function isRecord(value: unknown): value is JsonRecord {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function canonicalize(value: unknown, seen = new WeakSet<object>()): unknown {
  if (
    value === null ||
    typeof value === "string" ||
    typeof value === "boolean"
  ) {
    return value;
  }
  if (typeof value === "number") {
    if (!Number.isFinite(value)) {
      throw new Error(
        "OPENPENCIL_COMPONENT_ONTOLOGY_CANDIDATE_NON_FINITE_NUMBER",
      );
    }
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((entry) => canonicalize(entry, seen));
  }
  if (!isRecord(value)) {
    throw new Error(
      "OPENPENCIL_COMPONENT_ONTOLOGY_CANDIDATE_CANONICAL_JSON_INVALID",
    );
  }
  if (seen.has(value)) {
    throw new Error(
      "OPENPENCIL_COMPONENT_ONTOLOGY_CANDIDATE_CANONICAL_JSON_CYCLE",
    );
  }
  seen.add(value);
  const result: JsonRecord = {};
  for (const key of Object.keys(value).sort()) {
    const child = value[key];
    if (child !== undefined) result[key] = canonicalize(child, seen);
  }
  seen.delete(value);
  return result;
}

function canonicalJson(value: unknown): string {
  return JSON.stringify(canonicalize(value));
}

function canonicalSort<T>(values: readonly T[]): T[] {
  return [...values].sort((left, right) => {
    const a = canonicalJson(left);
    const b = canonicalJson(right);
    return a === b ? 0 : a < b ? -1 : 1;
  });
}

function uniqueCanonical<T>(values: readonly T[]): T[] {
  const result: T[] = [];
  let previous: string | undefined;
  for (const value of canonicalSort(values)) {
    const encoded = canonicalJson(value);
    if (encoded !== previous) result.push(value);
    previous = encoded;
  }
  return result;
}

function sha256(value: unknown): Sha256 {
  return `sha256:${nodeCrypto().createHash("sha256")
    .update(canonicalJson(value), "utf8")
    .digest("hex")}`;
}

function hmacTuple(key: Uint8Array, tuple: readonly unknown[]): OpaqueId {
  return `hmac-sha256:${nodeCrypto().createHmac("sha256", key)
    .update(canonicalJson(tuple), "utf8")
    .digest("hex")}`;
}

function keyedId(
  key: Uint8Array,
  domainTag: string,
  canonicalTypedValue: unknown,
): OpaqueId {
  return hmacTuple(key, [NAMESPACE, domainTag, canonicalTypedValue]);
}

function sourceString(value: unknown, code: string): string {
  if (typeof value !== "string" || value.length === 0) throw new Error(code);
  return value.normalize("NFKC");
}

function primitive(value: unknown, code: string): Primitive {
  if (value === null || typeof value === "boolean") return value;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") return value.normalize("NFKC");
  throw new Error(code);
}

function typedPrimitive(value: Primitive): string {
  if (value === null) return "null";
  if (typeof value === "boolean") return `boolean:${String(value)}`;
  if (typeof value === "number") return `number:${JSON.stringify(value)}`;
  return `string:${value.normalize("NFKC")}`;
}

function recordArray(value: unknown, code: string): JsonRecord[] {
  if (value === undefined || value === null) return [];
  if (!Array.isArray(value) || value.some((entry) => !isRecord(entry))) {
    throw new Error(code);
  }
  return value as JsonRecord[];
}

function propertyTypeCode(value: unknown): string {
  const normalized =
    typeof value === "string"
      ? value
          .normalize("NFKC")
          .toLowerCase()
          .replace(/[_\s]+/g, "-")
      : "";
  if (normalized === "boolean") return "property-type:boolean";
  if (normalized === "instance-swap" || normalized === "instanceswap") {
    return "property-type:instance-swap";
  }
  if (normalized === "text") return "property-type:text";
  if (normalized === "variant") return "property-type:variant";
  if (normalized === "number") return "property-type:number";
  return "property-type:unknown";
}

function localId(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function componentSemanticGroup(
  raw: JsonRecord,
  pageSubject: OpaqueId,
  key: Uint8Array,
): OpaqueId {
  const componentName = sourceString(
    raw.name,
    "OPENPENCIL_COMPONENT_ONTOLOGY_CANDIDATE_COMPONENT_NAME_REQUIRED",
  );
  const componentNameId = keyedId(
    key,
    "component-name",
    typedPrimitive(componentName),
  );
  const properties = recordArray(
    raw.propertyDefinitions,
    "OPENPENCIL_COMPONENT_ONTOLOGY_CANDIDATE_READBACK_INVALID",
  ).map((property) => {
    const name = sourceString(
      property.name,
      "OPENPENCIL_COMPONENT_ONTOLOGY_CANDIDATE_PROPERTY_NAME_REQUIRED",
    );
    const nameId = keyedId(key, "property-name", typedPrimitive(name));
    const typeCode = propertyTypeCode(property.type);
    const defaultObservationId =
      property.defaultValue === undefined
        ? null
        : keyedId(
            key,
            "opaque-observation:property-default-value",
            typedPrimitive(
              primitive(
                property.defaultValue,
                "OPENPENCIL_COMPONENT_ONTOLOGY_CANDIDATE_PROPERTY_VALUE_INVALID",
              ),
            ),
          );
    return {
      raw: property,
      name,
      nameId,
      signatureId: keyedId(key, "property-structural-signature", [
        nameId,
        typeCode,
        defaultObservationId,
      ]),
    };
  });
  const propertiesById = new Map<string, (typeof properties)[number][]>();
  const propertiesByName = new Map<string, (typeof properties)[number][]>();
  for (const property of properties) {
    for (const id of [
      localId(property.raw.id),
      localId(property.raw.nodeId),
      localId(property.raw.surfaceId),
      localId(property.raw.modeId),
    ]) {
      if (!id) continue;
      propertiesById.set(id, [...(propertiesById.get(id) ?? []), property]);
    }
    propertiesByName.set(property.name, [
      ...(propertiesByName.get(property.name) ?? []),
      property,
    ]);
  }
  const requireProperty = (
    candidates: (typeof properties)[number][] | undefined,
  ): (typeof properties)[number] => {
    if (
      !candidates ||
      candidates.length === 0 ||
      new Set(candidates.map((entry) => entry.signatureId)).size !== 1
    ) {
      throw new Error(
        "OPENPENCIL_COMPONENT_ONTOLOGY_CANDIDATE_PROPERTY_BINDING_INVALID",
      );
    }
    return candidates[0]!;
  };
  const variants = recordArray(
    raw.variantSamples,
    "OPENPENCIL_COMPONENT_ONTOLOGY_CANDIDATE_READBACK_INVALID",
  ).map((variant) => {
    const values = isRecord(
      variant.componentPropertyValues ?? variant.variantProperties,
    )
      ? ((variant.componentPropertyValues ??
          variant.variantProperties) as JsonRecord)
      : {};
    const specs = recordArray(
      variant.variantPropSpecs,
      "OPENPENCIL_COMPONENT_ONTOLOGY_CANDIDATE_READBACK_INVALID",
    );
    const pairs =
      specs.length > 0
        ? specs.map((spec) => {
            const propertyId = localId(spec.propDefId);
            if (!propertyId) {
              throw new Error(
                "OPENPENCIL_COMPONENT_ONTOLOGY_CANDIDATE_PROPERTY_BINDING_INVALID",
              );
            }
            return [
              requireProperty(propertiesById.get(propertyId)),
              spec.value,
            ] as const;
          })
        : Object.entries(values).map(
            ([name, value]) =>
              [
                requireProperty(propertiesByName.get(name.normalize("NFKC"))),
                value,
              ] as const,
          );
    const pairIds = pairs.map(([property, value]) => {
      const observationId = keyedId(
        key,
        "opaque-observation:variant-property-value",
        typedPrimitive(
          primitive(
            value,
            "OPENPENCIL_COMPONENT_ONTOLOGY_CANDIDATE_PROPERTY_VALUE_INVALID",
          ),
        ),
      );
      return keyedId(key, "property-value-pair", [
        property.nameId,
        observationId,
      ]);
    });
    return keyedId(
      key,
      "variant-structural-signature",
      uniqueCanonical(pairIds),
    );
  });
  return keyedId(key, "component-set", [
    pageSubject,
    componentNameId,
    uniqueCanonical(properties.map((entry) => entry.signatureId)),
    uniqueCanonical(variants),
  ]);
}

function relationTargets(
  fact: StructuralFact,
  role: StructuralFact["relations"][number]["role"],
): OpaqueId[] {
  return fact.relations
    .filter((entry) => entry.role === role)
    .map((entry) => entry.targetSubjectOpaqueId);
}

function stripSelectionLocalIdentity(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stripSelectionLocalIdentity);
  if (!isRecord(value)) return value;
  return Object.fromEntries(
    Object.entries(value)
      .filter(
        ([key]) =>
          !/^(?:source)?nodeId$|^graphNodeId$|^figmaNodeId$|^sourceLocator$/i.test(
            key,
          ),
      )
      .map(([key, child]) => [key, stripSelectionLocalIdentity(child)]),
  );
}

async function selectionFactHash(
  member: SourceStructureSelectionMemberV1,
  raw: JsonRecord,
): Promise<string> {
  return admissionContentHash({
    kind: member.kind,
    structuralPath: member.structuralPath,
    fact: stripSelectionLocalIdentity(raw),
  });
}

async function selectedRawComponents(
  readback: unknown,
  members: readonly SourceStructureSelectionMemberV1[],
): Promise<JsonRecord[]> {
  if (!isRecord(readback)) {
    throw new Error("OPENPENCIL_COMPONENT_ONTOLOGY_CANDIDATE_READBACK_INVALID");
  }
  const ontology = isRecord(readback.uiOntology) ? readback.uiOntology : {};
  const rawComponents = recordArray(
    ontology.componentSets,
    "OPENPENCIL_COMPONENT_ONTOLOGY_CANDIDATE_READBACK_INVALID",
  );
  const selected: JsonRecord[] = [];
  for (const member of members) {
    const [pageName, axis, componentName] = member.structuralPath;
    if (
      member.structuralPath.length !== 3 ||
      axis !== "componentSets" ||
      !pageName ||
      !componentName
    ) {
      continue;
    }
    const pathMatches = rawComponents.filter(
      (raw) =>
        typeof raw.pageName === "string" &&
        typeof raw.name === "string" &&
        raw.pageName.normalize("NFKC").trim() === pageName &&
        raw.name.normalize("NFKC").trim() === componentName,
    );
    const hashMatches: JsonRecord[] = [];
    for (const raw of pathMatches) {
      if ((await selectionFactHash(member, raw)) === member.sourceFactHash) {
        hashMatches.push(raw);
      }
    }
    if (hashMatches.length !== 1) {
      throw new Error(
        "OPENPENCIL_COMPONENT_ONTOLOGY_CANDIDATE_SELECTION_MEMBER_INVALID",
      );
    }
    selected.push(hashMatches[0]!);
  }
  if (selected.length === 0) {
    throw new Error("OPENPENCIL_COMPONENT_ONTOLOGY_CANDIDATE_SELECTION_EMPTY");
  }
  return selected;
}

function sameStringSet(left: readonly string[], right: readonly string[]) {
  return (
    canonicalJson([...new Set(left)].sort()) ===
    canonicalJson([...new Set(right)].sort())
  );
}

export interface DeriveSelectedComponentSetOpaqueIdsV2Input {
  readback: unknown;
  selection: SourceStructureSelectionV1;
  projectionKey: Uint8Array;
  structuralFacts: readonly StructuralFact[];
}

export async function deriveSelectedComponentSetOpaqueIdsV2(
  input: DeriveSelectedComponentSetOpaqueIdsV2Input,
): Promise<OpaqueId[]> {
  const selection = SourceStructureSelectionV1Schema.safeParse(input.selection);
  if (
    !selection.success ||
    !resolveOpenPencilSourceStructureSelection(
      input.readback as Parameters<
        typeof resolveOpenPencilSourceStructureSelection
      >[0],
      selection.data,
    ).passed
  ) {
    throw new Error(
      "OPENPENCIL_COMPONENT_ONTOLOGY_CANDIDATE_SELECTION_INVALID",
    );
  }
  const selectedRaw = await selectedRawComponents(
    input.readback,
    selection.data.members,
  );
  const componentFacts = input.structuralFacts.filter(
    (fact) => fact.factType === "component-set",
  );
  const ids = selectedRaw.map((raw) => {
    const matches = componentFacts.filter((fact) => {
      const pageTargets = relationTargets(fact, "belongs-to-page");
      return (
        pageTargets.length === 1 &&
        componentSemanticGroup(raw, pageTargets[0]!, input.projectionKey) ===
          fact.semanticGroupOpaqueId
      );
    });
    if (matches.length !== 1) {
      throw new Error(
        "OPENPENCIL_COMPONENT_ONTOLOGY_CANDIDATE_COMPONENT_JOIN_INVALID",
      );
    }
    return matches[0]!.semanticGroupOpaqueId;
  });
  return [...new Set(ids)].sort();
}

export async function buildOpenPencilComponentOntologyCandidateV2(
  input: OpenPencilComponentOntologyCandidateInputV2,
): Promise<OpenPencilComponentOntologyCandidateV2> {
  const preflight = await preflightOpenPencilComponentBoundaryV2(input);
  if (
    !Array.isArray(input.allowedComponentSetOpaqueIds) ||
    input.allowedComponentSetOpaqueIds.length === 0 ||
    input.allowedComponentSetOpaqueIds.some(
      (value) => typeof value !== "string" || !OPAQUE_PATTERN.test(value),
    ) ||
    new Set(input.allowedComponentSetOpaqueIds).size !==
      input.allowedComponentSetOpaqueIds.length
  ) {
    throw new Error(
      "OPENPENCIL_COMPONENT_ONTOLOGY_CANDIDATE_ALLOWED_IDS_INVALID",
    );
  }
  const allowedComponentSetOpaqueIds = [...input.allowedComponentSetOpaqueIds];
  const preflightFacts = getPreflightOpenPencilOntologyFactsV2(
    input,
    preflight,
  );
  const componentFacts = preflightFacts.filter(
    (fact) => fact.factType === "component-set",
  );
  for (const opaqueId of allowedComponentSetOpaqueIds) {
    if (
      componentFacts.filter((fact) => fact.semanticGroupOpaqueId === opaqueId)
        .length !== 1
    ) {
      throw new Error(
        "OPENPENCIL_COMPONENT_ONTOLOGY_CANDIDATE_ALLOWED_IDS_MISMATCH",
      );
    }
  }

  const reuseReceipt = isRecord(input.libraryReuseAdmissionReceipt)
    ? input.libraryReuseAdmissionReceipt
    : {};
  const selection = SourceStructureSelectionV1Schema.safeParse(
    reuseReceipt.selection,
  );
  if (!selection.success) {
    throw new Error(
      "OPENPENCIL_COMPONENT_ONTOLOGY_CANDIDATE_SELECTION_INVALID",
    );
  }
  const verified = openVerifiedOpenPencilComponentReadbackV2(input, preflight);
  const projectionKey = verified.projectionKey;
  if (
    !resolveOpenPencilSourceStructureSelection(
      verified.readback as Parameters<
        typeof resolveOpenPencilSourceStructureSelection
      >[0],
      selection.data,
    ).passed
  ) {
    throw new Error(
      "OPENPENCIL_COMPONENT_ONTOLOGY_CANDIDATE_SELECTION_INVALID",
    );
  }
  const selectedRaw = await selectedRawComponents(
    verified.readback,
    selection.data.members,
  );
  const facts = [...verified.facts];
  const verifiedComponentFacts = facts.filter(
    (fact) => fact.factType === "component-set",
  );
  const authorizedComponentSetOpaqueIds =
    await deriveSelectedComponentSetOpaqueIdsV2({
      readback: verified.readback,
      selection: selection.data,
      projectionKey,
      structuralFacts: facts,
    });
  if (
    !sameStringSet(
      authorizedComponentSetOpaqueIds,
      allowedComponentSetOpaqueIds,
    )
  ) {
    throw new Error(
      "OPENPENCIL_COMPONENT_ONTOLOGY_CANDIDATE_SELECTION_SET_MISMATCH",
    );
  }
  const selectedEntries = selectedRaw.map((raw) => {
    const matches = verifiedComponentFacts.filter((fact) => {
      const pageTargets = relationTargets(fact, "belongs-to-page");
      return (
        pageTargets.length === 1 &&
        componentSemanticGroup(raw, pageTargets[0]!, projectionKey) ===
          fact.semanticGroupOpaqueId
      );
    });
    if (matches.length !== 1) {
      throw new Error(
        "OPENPENCIL_COMPONENT_ONTOLOGY_CANDIDATE_COMPONENT_JOIN_INVALID",
      );
    }
    return { raw, fact: matches[0]! };
  });
  const selectedIds = selectedEntries.map(
    ({ fact }) => fact.semanticGroupOpaqueId,
  );
  if (!sameStringSet(selectedIds, allowedComponentSetOpaqueIds)) {
    throw new Error(
      "OPENPENCIL_COMPONENT_ONTOLOGY_CANDIDATE_SELECTION_SET_MISMATCH",
    );
  }

  const selectedBySubject = new Map<
    OpaqueId,
    Array<(typeof selectedEntries)[number]>
  >();
  for (const entry of selectedEntries) {
    selectedBySubject.set(entry.fact.subjectOpaqueId, [
      ...(selectedBySubject.get(entry.fact.subjectOpaqueId) ?? []),
      entry,
    ]);
  }
  const designations: OpenPencilComponentOntologyCandidateV2["designations"] =
    [];
  for (const [subject, entries] of selectedBySubject) {
    const subjectFacts = verifiedComponentFacts.filter(
      (candidate) => candidate.subjectOpaqueId === subject,
    );
    if (subjectFacts.length !== 1) {
      throw new Error(
        "OPENPENCIL_COMPONENT_ONTOLOGY_CANDIDATE_COMPONENT_JOIN_INVALID",
      );
    }
    const fact = subjectFacts[0]!;
    const pageTargets = relationTargets(fact, "belongs-to-page");
    if (pageTargets.length !== 1) {
      throw new Error(
        "OPENPENCIL_COMPONENT_ONTOLOGY_CANDIDATE_PAGE_RELATION_INVALID",
      );
    }
    const pageFacts = facts.filter(
      (candidate) =>
        candidate.factType === "page" &&
        candidate.subjectOpaqueId === pageTargets[0],
    );
    if (pageFacts.length !== 1) {
      throw new Error(
        "OPENPENCIL_COMPONENT_ONTOLOGY_CANDIDATE_PAGE_JOIN_INVALID",
      );
    }
    const propertyAxisSemanticRefs = uniqueCanonical(
      facts
        .filter(
          (candidate) =>
            candidate.factType === "component-property" &&
            relationTargets(candidate, "belongs-to-component-set").includes(
              fact.subjectOpaqueId,
            ),
        )
        .map((candidate) => candidate.semanticGroupOpaqueId),
    );
    const variantMetrics = fact.metrics.filter(
      (metric) => metric.role === "variant-count",
    );
    if (
      variantMetrics.length !== 1 ||
      !Number.isInteger(variantMetrics[0]!.value) ||
      variantMetrics[0]!.value < 0
    ) {
      throw new Error(
        "OPENPENCIL_COMPONENT_ONTOLOGY_CANDIDATE_VARIANT_COUNT_INVALID",
      );
    }
    const tuples = entries.map(({ raw }) => {
      const role = mapComponentRole(
        sourceString(
          raw.name,
          "OPENPENCIL_COMPONENT_ONTOLOGY_CANDIDATE_COMPONENT_NAME_REQUIRED",
        ),
      );
      const disposition = role.status === "exact" ? "candidate" : role.status;
      return {
        sourceSemanticRef: fact.semanticGroupOpaqueId,
        pageSemanticRef: pageFacts[0]!.semanticGroupOpaqueId,
        canonicalRole: role.role,
        disposition,
        observedVariantCount: variantMetrics[0]!.value,
        propertyAxisSemanticRefs,
      } as const;
    });
    const converged = uniqueCanonical(tuples);
    if (converged.length !== 1 || entries.length !== fact.multiplicity) {
      throw new Error(
        "OPENPENCIL_COMPONENT_ONTOLOGY_CANDIDATE_CONVERGENCE_INVALID",
      );
    }
    const tuple = converged[0]!;
    designations.push({
      ...tuple,
      semanticSignature: sha256([
        "cogo.openpencil.component-ontology-candidate.v2",
        tuple.sourceSemanticRef,
        tuple.pageSemanticRef,
        tuple.canonicalRole ?? "null",
        tuple.disposition,
        tuple.observedVariantCount,
        tuple.propertyAxisSemanticRefs,
        verified.sourceRefs,
      ]),
    });
  }

  return {
    schemaVersion: "cogo.openpencil-component-ontology-candidate.v2",
    designations: canonicalSort(designations),
    sourceRefs: verified.sourceRefs,
    authority: {
      candidateOnly: true,
      generationEligible: false,
    },
  };
}

export async function verifyOpenPencilComponentOntologyCandidateV2(
  input: OpenPencilComponentOntologyCandidateInputV2,
  candidate: unknown,
): Promise<{ valid: boolean; issues: string[] }> {
  try {
    const expected = await buildOpenPencilComponentOntologyCandidateV2(input);
    if (canonicalJson(expected) !== canonicalJson(candidate)) {
      return {
        valid: false,
        issues: [
          "OPENPENCIL_COMPONENT_ONTOLOGY_CANDIDATE_RECOMPUTATION_MISMATCH",
        ],
      };
    }
    return { valid: true, issues: [] };
  } catch (error) {
    return {
      valid: false,
      issues: [
        error instanceof Error
          ? error.message
          : "OPENPENCIL_COMPONENT_ONTOLOGY_CANDIDATE_VERIFICATION_FAILED",
      ],
    };
  }
}
